import React, { FC, useEffect, useState } from 'react'
import { RouteComponentProps } from "@reach/router"
import _ from 'lodash'
import Isvg from 'react-inlinesvg'
// Components
import { Toolbar } from "components/subnav"
import RecoveryPoolsTable from "components/recoveries/recovery-pools/recovery-pools-table"
import { WithTip } from 'components/tooltip/with-tip'
// Data
import api_match from "data/interface/api"
import recoveryPoolsService, { RecoveryPool } from "data/recovery-pools"
import propertyService, { Property } from 'data/property'
import tenantGroupService, { TenantGroup } from 'data/tenant-group'
import lineItemsService, { LineItem } from "data/line-items"
import tenantContributionsService, { TenantContribution } from 'data/tenant-contributions'
import { alphanum } from "components/table/lib/sort"
import { ContextRow } from './recovery-pools-table'
import toggles from "feature-toggles"
//Styles
import { RecoveryPools_s } from "components/recoveries/recovery-pools/recovery-pools-styles"
import { AddIcon } from "images/icons/tools"

const tableId = "recovery-pools-table"

const RecoveryPools: FC<RouteComponentProps> = (props) => {
  const [data, setData] = useState<RecoveryPool[]>([])
  const [initSort, setInitSort] = useState<boolean>(false)
  const [property, setProperty] = useState<Property|null>(null);
  const [propertyId, setPropertyId] = useState(propertyService.getStoredPropertyId())
  const [tenantGroups, setTenantGroups] = useState<TenantGroup[]>([]);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [tenantContributions, setTenantContributions] = useState<TenantContribution[]>([])
  const [showSelected, setShowSelected] = useState<boolean>(false)
  const [selected, setSelected] = useState<ContextRow[]>([]);

  useEffect(() => {

    const recoveryPoolsSubscription = recoveryPoolsService.stream().subscribe(data => {
      api_match(
        data,
        init  => setData(init.value),
        ok    => {
          if(!initSort && ok.value.length) { // sort only on first load
            setData([...ok.value].sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))))
            setInitSort(true)
          } else {
            setData(ok.value)
          }
        },
        err   => {
          setData(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.value.length > 0){
        const currentProperty = _.find(data.value, {propertyid: data.current});
        setProperty(currentProperty);
      }
      switch (data.state) {
        case 'ok':
          setPropertyId(data.current)
          break
      }
    })

    const tenantGroupSubscription = tenantGroupService.stream().subscribe(data => {
      api_match(
        data,
        init  => setTenantGroups(init.value),
        ok    => {
          if(ok.value.length) { // sort only on first load
            setTenantGroups(ok.value)
          } else {
            setTenantGroups(ok.value)
          }
        },
        err   => {
          setTenantGroups(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const lineItemsSubscription = lineItemsService.stream().subscribe(data => {
      api_match(
        data,
        init  => setLineItems(init.value),
        ok    => {
          setLineItems(ok.value)
        },
        err   => {
          setLineItems(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const tenantContributionsSubscription = tenantContributionsService.stream().subscribe(data => {
      api_match(
        data,
        init  => setTenantContributions(init.value),
        ok    => {
          setTenantContributions(ok.value)
        },
        err   => {
          setTenantContributions(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    return function cleanup() {
      recoveryPoolsSubscription.unsubscribe()
      propertySubscription.unsubscribe()
      tenantGroupSubscription.unsubscribe()
      lineItemsSubscription.unsubscribe()
      tenantContributionsSubscription.unsubscribe()
    }
  }, [initSort])

  useEffect(() => {
    if (_.isNil(propertyId)) return

    recoveryPoolsService.fetchRecoveryPools(propertyId)
    propertyService.fetchProperty(propertyId)
    tenantGroupService.fetchTenantGroups(propertyId)
    lineItemsService.fetchLineItems(propertyId)
    tenantContributionsService.fetchTenantContributions(propertyId)
  }, [propertyId])

  const Add = () => {
    return (
      <a onClick={() => recoveryPoolsService.create(propertyService.getStoredPropertyId())}>
        <Isvg src={AddIcon} width="20px" />
      </a>
    )
  }

  const [deleteFlag, setDeleteFlag] = useState(0)
  const Delete = () => {
    return (
      <WithTip position="bottom">
        <span>Delete Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput')  ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setDeleteFlag(deleteFlag+1)
        }}>
          <span className="p-menuitem-icon pi pi-fw pi-trash"></span>
        </a>
      </WithTip>
    )
  }

  const [pasteFlag, setPasteFlag] = useState(0)
  const Paste = () => {
    return (
      <WithTip position="bottom">
        <span>Paste to Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput') ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setPasteFlag(pasteFlag+1)
          }
        }>
          <span className="p-menuitem-icon pi pi-fw pi-clone"></span>
        </a>
      </WithTip>
    )
  }

  const ShowSelected = () => (
    <WithTip position="bottom">
      <span>Show {showSelected ? 'All' : 'Selected'} Rows</span>
      <a className={selected.length ? '' : 'disabled'}
        onClick={() => {
          if (!selected.length) return
          setShowSelected(!showSelected)
        }}>
        <span className={showSelected ? 'pi pi-eye-slash' : 'pi pi-eye'} style={{height: '18px', width: '18px'}}></span>
      </a>
    </WithTip>
  )

  useEffect(() => {
    setSelected(_.intersectionBy(data, selected, 'recoverpoolid'))
  }, [data])

  useEffect(() => {
    if (showSelected && _.isEmpty(selected)) setShowSelected(false)
  }, [selected])

  const _data = showSelected ? selected : data

  return (
    <RecoveryPools_s>
      <Toolbar>
        <Add />
        {toggles.multiselect() && <>
          <Paste />
          <Delete />
          <ShowSelected />
        </>}
      </Toolbar>
      { data && property && tenantGroups && lineItems && tenantContributions && <RecoveryPoolsTable tableId={tableId} data={_data} property={property} tenantGroups={tenantGroups} lineItems={lineItems} tenantContributions={tenantContributions} selectedState={[selected, setSelected]} flags={{deleteFlag, pasteFlag}} /> }
    </RecoveryPools_s>
  )
}

export default RecoveryPools
